import CompositeError from '../../common/utils/CompositeError';
import Node from './Node';
import { dispatchDetectionEvent } from '../../events/dispatch';
import wrapRetry from '../../common/utils/wrapRetry';
import {
  ComponentCode,
  DetectionType,
  StatusCode
} from '../../reporter/constants';
import sendPeerConfigDiagnostic, {
  ConnectionError,
  pushIf
} from './sendPeerConfigDiagnostic';

export const PEER_STARTUP_RETRIES = 3;
const componentCode = ComponentCode.SIGNAL;
const status = StatusCode.CONNECTION_ERROR;
const type = DetectionType.RUNTIME;

/**
 * Fetch Agent configuration from server.
 *
 * @param configUrl - url to fetch config in GET request.
 * @returns - promise containing config data or error
 */
export default async function getPeerConfig(
  configUrl: string,
  token: string,
  node: Node,
  headers: Record<string, string>
): Promise<Node> {
  return wrapRetry(_getPeerConfig, PEER_STARTUP_RETRIES, shouldRetry)(
    configUrl,
    token,
    node,
    headers
  ).catch(e => throwDetectionError<Node>(e));
}

export const throwDetectionError = <T>(err: CompositeError): Promise<T> => {
  sendPeerConfigDiagnostic(err);
  const e = err.lastError() as ConnectionError;
  const statusCode = e?.status;
  const error = (statusCode && { statusCode }) || undefined;
  const data = { componentCode, error };
  dispatchDetectionEvent({ data, status, type });
  return Promise.reject(e);
};

const RETRY_CODES = [
  0, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524, 598, 599
];

export const shouldRetry = (error: ConnectionError): boolean => {
  const status = error.status ?? 0;
  // Only retry if there is no response or a response which has a code in the retry list.
  return !error || RETRY_CODES.indexOf(status) > -1;
};

export const _getPeerConfig = async (
  configUrl: string,
  token: string,
  node: Node,
  headers: Record<string, string>
): Promise<Node> => {
  const start = ConnectionError.now();
  const fetchPromise = fetch(configUrl, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=utf-8',
      'Access-Control-Allow-Origin': '*'
    },
    body: JSON.stringify(node)
  });
  return await fetchPromise
    .then(resp => {
      if (resp.ok) {
        return resp.json();
      } else {
        const { status, statusText: diagText } = resp;
        const data = { start, status, diagText, url: configUrl };
        switch (resp.status) {
          case 403:
          case 401:
            return Promise.reject(
              new ConnectionError({ ...data, message: `with token: ${token}` })
            );
          default:
            return Promise.reject(new ConnectionError(data));
        }
      }
    })
    .catch(e => {
      if (e instanceof ConnectionError) {
        return Promise.reject(e);
      }
      const arr: string[] = [];
      pushIf(arr, e?.message ?? e?.statusText);
      pushIf(arr, e?.name, 'name');
      const data = {
        start,
        url: configUrl,
        status: e?.status ?? e?.code,
        diagText: arr.length > 0 ? arr.join('; ') : undefined
      };
      return Promise.reject(new ConnectionError(data));
    });
};
